import type { AxiosResponse } from 'axios';

/**
 * Compose namespaced cache keys.
 *
 * @param namespace Optional namespace segment
 * @param group Optional group segment
 * @param key Final key segment (required)
 * @returns Composed key like "ns:grp:key", omitting empty segments
 */
export function composeScopedKey(
  namespace: string | undefined,
  group: string | undefined,
  key: string
): string {
  const parts = [namespace, group, key].filter(
    (v) => v != null && String(v).length > 0
  );
  return parts.join(':');
}

/**
 * Estimate size in bytes of a JSON-serializable value.
 *
 * @param value Any serializable value
 * @returns Approximate byte length
 */
export function approxSize(value: unknown): number {
  try {
    return JSON.stringify(value).length;
  } catch {
    return 0;
  }
}

/**
 * Build a lightweight AxiosResponse for cached hits.
 *
 * @typeParam T Data shape
 * @param data Response data to embed
 * @returns AxiosResponse-like object
 */
export function makeCachedAxiosResponse<T>(data: T): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText: 'OK (cache)',
    headers: {},
    config: {} as any,
  } as AxiosResponse<T>;
}

/**
 * Metadata used for eviction calculations.
 */
export interface MetaEntry {
  key: string;
  last: number; // lastAccessed epoch ms
  expiresAt: number; // epoch ms
  size: number; // approx bytes
}

/**
 * Compute eviction keys: expired first, then least recent by last access.
 *
 * @param meta List of meta entries
 * @param now Current timestamp (ms)
 * @param evictFraction Fraction of records to evict (clamped 0.1..0.9)
 * @returns Array of keys to evict
 */
export function computeEvictionKeys(
  meta: MetaEntry[],
  now: number,
  evictFraction: number
): string[] {
  if (!meta.length) return [];
  const fraction = Math.min(Math.max(evictFraction, 0.1), 0.9);
  const sorted = [...meta].sort((a, b) => {
    const aExpired = a.expiresAt < now ? -1 : 1;
    const bExpired = b.expiresAt < now ? -1 : 1;
    if (aExpired !== bExpired) return aExpired - bExpired; // expired first
    return (a.last || 0) - (b.last || 0); // least recent first
  });
  const evictCount = Math.max(1, Math.ceil(sorted.length * fraction));
  return sorted.slice(0, evictCount).map((m) => m.key);
}

const helpers = {
  composeScopedKey,
  approxSize,
  makeCachedAxiosResponse,
  computeEvictionKeys,
};

export default helpers;
